import React, { useState } from "react";
import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

/** Column shape needed for Excel headers; accepts Ant Design ColumnsType. */
type ExportColumn = {
  title?: unknown;
  key?: React.Key;
};

interface ExportTableButtonProps {
  // ASSUMPTION: Row objects are plain records keyed by column.key.
  // Interfaces without index signatures (CatalogExportRow, DetailLog, etc.)
  // are valid at runtime and must be accepted here.
  data: object[];
  columns: ExportColumn[];
  disabled?: boolean;
  variant?: "primary" | "default";
  /**
   * When set, fetches full dataset before writing the file.
   * Falls back to `data` (current page) if omitted.
   */
  fetchAllData?: () => Promise<object[]>;
  fileNamePrefix?: string;
}

const asRecord = (row: object): Record<string, unknown> =>
  row as Record<string, unknown>;

const buildWorkbookRows = (
  rows: object[],
  columns: ExportColumn[]
): Record<string, unknown>[] =>
  rows.map((row) => {
    const source = asRecord(row);
    const newRow: Record<string, unknown> = {};
    columns.forEach((column) => {
      const title =
        typeof column.title === "string"
          ? column.title
          : String(column.key ?? "");
      const key = String(column.key ?? "");
      if (!key) return;
      newRow[title || key] = source[key];
    });
    return newRow;
  });

const downloadWorkbook = (
  rows: object[],
  columns: ExportColumn[],
  fileNamePrefix: string
): void => {
  if (rows.length === 0) {
    message.warning("No hay datos para exportar.");
    return;
  }

  const currentDate = new Date()
    .toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/[/:, ]/g, "_");

  const fileName = `${fileNamePrefix}_${currentDate}.xlsx`;
  const ws = XLSX.utils.json_to_sheet(buildWorkbookRows(rows, columns));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, fileName);
};

const ExportTableButton: React.FC<ExportTableButtonProps> = ({
  data,
  columns,
  disabled,
  variant = "primary",
  fetchAllData,
  fileNamePrefix = "Documento",
}) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;

    try {
      if (fetchAllData) {
        setExporting(true);
        const rows = await fetchAllData();
        downloadWorkbook(rows, columns, fileNamePrefix);
        return;
      }

      downloadWorkbook(data, columns, fileNamePrefix);
    } catch (err) {
      message.error(
        err instanceof Error
          ? err.message
          : "No se pudo exportar la tabla."
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      type={variant}
      icon={<DownloadOutlined />}
      onClick={() => {
        void handleExport();
      }}
      disabled={disabled || exporting}
      loading={exporting}
    >
      {exporting ? "Exportando…" : "Exportar Tabla"}
    </Button>
  );
};

export default ExportTableButton;
